import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import FilterList from '@mui/icons-material/FilterList';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { poiState, PointOfInterestTab } from '@/components/Analysis/modules/pointOfInterest';
import { EquipmentsTab } from '@/components/Analysis/shared/EquipmentsTab';
import * as analysisState from '@/components/Analysis/state';
import {
  DataLoaderPointOfInterestQueryObject,
  DrawersProps,
  PointOfInterestChecked,
  PointOfInterestWithCuboids,
  SelectedTab,
} from '@/types';
import { SideDrawer } from '@/components/shared/SideDrawer';
import useAbility from '@/hooks/useAbility';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useAnalysisCuboidActions } from '../../modules/cuboids/hooks/useAnalysisCuboidActions';
import { abyssColors } from '@/theme/colors';
import { CustomBlocksTab } from '../../modules/CustomBlocks';
import { useFeatureFlag } from '@/hooks';
import { TabType } from '@/components/shared/BasicTabs/BasicTabs';
import * as customBlocksState from '@/components/Analysis/modules/CustomBlocks/state';
import { FloatingLine, tabsStyles } from './styles';
import { ComponentsTab } from '../../ReviewScreen/ComponentsTab';
import Stack from '@mui/material/Stack';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';

const poiExistsInBlock = (poi: DataLoaderPointOfInterestQueryObject[number], blockId?: string) => {
  if (!blockId) return false;
  return (
    poi?.paintRegions?.some((paintRegion) => paintRegion.id === blockId) ||
    poi?.paintRegion?.id === blockId
  );
};

export const AssemblyTabs = ({ allClosed, isOpen, handleOpen }: DrawersProps) => {
  const setFilteredAssemblyIds = useSetAtom(analysisState.filteredAssemblyIds);
  const [workscopeResetFocus, setWorkscopeResetFocus] = useState<boolean>(true);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);

  const allPOI = useAtomValue(poiState.structurePois);
  const isAlphaUser = useFeatureFlag('alpha-user');

  const blisterState = useAtomValue(poiState.blisterToAdd);

  const selectedAssemblyId = useAtomValue(analysisState.selectedAssemblyId);
  const filters = useAtomValue(analysisState.filters);
  const selectedSavedBlockId = useAtomValue(customBlocksState.selectedSavedBlockId);
  const selectedCustomBlock = useAtomValue(customBlocksState.selectedCustomBlock);
  const selectedSavedBlock = useAtomValue(customBlocksState.selectedSavedBlock);
  const filteredAssemblies = useAtomValue(analysisState.filteredAssemblies);

  const [poiFilteredByCheckbox, setPoiFilteredByCheckbox] = useAtom(poiState.poiFilteredByCheckbox); // const filteredAssemblies = useAtomValue(analysisState.filteredAssembliesByTemplateFields);

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(SelectedTab.COMPONENT);
  const { ability } = useAbility();
  const canReadPOI = ability.can('read', 'PointOfInterest');

  const { initializeCuboids } = useAnalysisCuboidActions();

  const selectedPointOfInterest = useAtomValue(poiState.selectedPointOfInterestId);

  const canViewReviewScreen = useFeatureFlag(['api-inspector', 'mi-engineer']);

  useEffect(() => {
    if (selectedPointOfInterest) {
      setSelectedTabIndex(SelectedTab.POINTS_OF_INTEREST);
    }
  }, [selectedPointOfInterest]);

  const changeTab = useCallback(
    (tabIndex: number) => {
      setWorkscopeResetFocus(selectedTabIndex !== SelectedTab.POINTS_OF_INTEREST);
      setSelectedTabIndex(tabIndex);
    },
    [selectedTabIndex]
  );

  useEffect(() => {
    if (selectedSavedBlockId) {
      setSelectedTabIndex(SelectedTab.CUSTOM_BLOCKS);
    }
  }, [selectedSavedBlockId]);

  const { rightDrawerWidth, doublePanelWidth } = useDrawerWidths();

  const drawerWidth = useMemo(() => {
    if (editPointOfInterest?.formState === 'Verify') return rightDrawerWidth;
    return blisterState?.state === 'UpdatingBlister' ? doublePanelWidth : rightDrawerWidth;
  }, [blisterState?.state, doublePanelWidth, rightDrawerWidth, editPointOfInterest?.formState]);

  // Only get the POIs that exists on the filtered assemblies, exluding the ones that are not in the filtered assemblies
  const filteredPois = useMemo(() => {
    if (!allPOI || allPOI.length === 0) {
      return [];
    }

    const { paintRegionIds, workpackIds } = filters ?? {};
    const paintRegionSet =
      paintRegionIds && paintRegionIds.length > 0 ? new Set(paintRegionIds) : undefined;
    const workpackSet = workpackIds && workpackIds.length > 0 ? new Set(workpackIds) : undefined;
    const filteredAssemblySet = new Set(filteredAssemblies?.map(({ id }) => id));
    return allPOI.filter((poi) => {
      if (selectedPointOfInterest && poi.id === selectedPointOfInterest) return true;

      return (
        (filteredAssemblySet.has(poi.assembly.id) &&
          (!paintRegionSet || !poi.paintRegion?.id || paintRegionSet?.has(poi.paintRegion?.id)) &&
          (!workpackSet || (poi.workpack?.id && workpackSet?.has(poi.workpack?.id)))) ||
        poi.assembly.id === selectedAssemblyId ||
        poiExistsInBlock(poi, selectedSavedBlock?.id) ||
        poiExistsInBlock(poi, selectedCustomBlock?.paintRegionId)
      );
    });
  }, [
    allPOI,
    filteredAssemblies,
    filters,
    selectedSavedBlock,
    selectedCustomBlock,
    selectedPointOfInterest,
    selectedAssemblyId,
  ]);

  // If an assembly is selected, only show the POIs for that assembly (takes precedence).
  // Otherwise show all POIs for the filtered assemblies.
  // Note: block selection no longer narrows POIs — the visibility box handles spatial context.
  const pois = useMemo(() => {
    return selectedAssemblyId
      ? filteredPois.filter(
          (poi) =>
            poi.assembly.id === selectedAssemblyId ||
            (selectedPointOfInterest && poi.id === selectedPointOfInterest)
        )
      : filteredPois;
  }, [filteredPois, selectedAssemblyId, selectedPointOfInterest]);

  const tabs = useMemo(() => {
    return [
      {
        index: SelectedTab.COMPONENT,
        label: 'Component',
        component: canViewReviewScreen ? (
          <ComponentsTab changeTab={changeTab} />
        ) : (
          <EquipmentsTab changeTab={changeTab} />
        ),
      },
      isAlphaUser
        ? {
            index: SelectedTab.CUSTOM_BLOCKS,
            label: (
              <Stack direction="row" alignItems="center" gap="0.8rem">
                <span>Blocks</span>
                <AbyssIcon iconName="beta" size="sm" />
              </Stack>
            ),
            component: <CustomBlocksTab resetFocus={workscopeResetFocus} />,
          }
        : undefined,
      {
        index: SelectedTab.POINTS_OF_INTEREST,
        label: 'Points of interest',
        component: (
          <PointOfInterestTab pois={pois as PointOfInterestChecked[]} changeTab={changeTab} />
        ),
      },
    ]
      .filter(Boolean)
      .filter((item) => item?.label !== 'Points of Interest' || canReadPOI) as TabType[];
  }, [canViewReviewScreen, changeTab, isAlphaUser, workscopeResetFocus, pois, canReadPOI]);

  useEffect(() => {
    // initialise the poiFilteredByCheckbox with all pois for this equipment/assembly
    // when an assembly is selected
    setPoiFilteredByCheckbox(pois as PointOfInterestChecked[]);
  }, [pois, setPoiFilteredByCheckbox]);

  useEffect(() => {
    if (poiFilteredByCheckbox) {
      initializeCuboids(poiFilteredByCheckbox as PointOfInterestWithCuboids[]);
    }
    // Adding initializeCuboids to the dependency array causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poiFilteredByCheckbox]);

  useEffect(() => {
    const filteredAssemblyIds = filteredAssemblies ? filteredAssemblies.map(({ id }) => id) : [];

    // this line is all that needs to be set to highlight the asset results in the spacial viewer
    // feel free to change if needed
    setFilteredAssemblyIds(filteredAssemblyIds);
  }, [filteredAssemblies, setFilteredAssemblyIds]);

  return (
    <SideDrawer
      allDrawersClosed={allClosed}
      icon={<FilterList />}
      position="right"
      width={drawerWidth}
      isOpen={isOpen}
      handleOpen={handleOpen}
      iconStyles={{ top: 10, zIndex: '102 !important' }}
      sxPaper={{ overflowY: 'hidden', border: 0 }}
    >
      <BasicTabs
        tabs={tabs}
        selectedTab={selectedTabIndex}
        tabsBackgroundColor={abyssColors.primary[50]}
        changeTab={changeTab}
        tabsStyles={tabsStyles}
        customSeperator={<FloatingLine />}
        fullWidth
        // renderingStrategy={canViewReviewScreen ? 'eager' : 'lazy'}
      />
    </SideDrawer>
  );
};
